import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getAllies, updateAlly, deleteAlly } from '../../services/adminService';

const inputStyle = {
  padding: '0.5rem 0.75rem',
  border: '1px solid #ddd',
  borderRadius: '8px',
  fontSize: '0.9rem',
  outline: 'none',
};

const PAGE_LIMIT = 20;

const AdminAlliesList = () => {
  const { session } = useAuth();
  const navigate = useNavigate();

  const [allies, setAllies]         = useState([]);
  const [total, setTotal]           = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage]             = useState(1);
  const [search, setSearch]         = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!session?.access_token) { setLoading(false); setError('No hay sesión activa.'); return; }
    let cancelled = false;
    setLoading(true);

    const params = { page, limit: PAGE_LIMIT };
    if (debouncedSearch) params.search = debouncedSearch;

    getAllies(session.access_token, params)
      .then((data) => {
        if (cancelled) return;
        setAllies(data.allies || []);
        setTotal(data.total || 0);
        setTotalPages(data.totalPages || 1);
        setLoading(false);
      })
      .catch((err) => { if (!cancelled) { setError(err.message); setLoading(false); } });

    return () => { cancelled = true; };
  }, [session?.access_token, page, debouncedSearch]);

  const handleToggleActive = async (ally) => {
    setTogglingId(ally.id);
    try {
      const updated = await updateAlly(session.access_token, ally.id, { is_active: !ally.is_active });
      setAllies((prev) => prev.map((a) => a.id === ally.id ? { ...a, is_active: updated.ally.is_active } : a));
    } catch (err) {
      alert(`Error al actualizar: ${err.message}`);
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (ally) => {
    if (!window.confirm(`¿Eliminar "${ally.name}"? Se eliminarán también sus productos. Esta acción no se puede deshacer.`)) return;
    setDeletingId(ally.id);
    try {
      await deleteAlly(session.access_token, ally.id);
      setAllies((prev) => prev.filter((a) => a.id !== ally.id));
      setTotal((t) => t - 1);
    } catch (err) {
      alert(`Error al eliminar: ${err.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="container" style={{ padding: '2rem 0 4rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
        <Link to="/admin" style={{ color: '#888', textDecoration: 'none', fontSize: '0.9rem' }}>← Panel</Link>
        <h1 style={{ fontFamily: "'Gambado Sans', sans-serif", fontSize: '1.6rem', color: '#333', margin: 0 }}>
          Aliados
        </h1>
        <span style={{ color: '#888', fontSize: '0.9rem', marginLeft: 'auto' }}>
          {total} aliado{total !== 1 ? 's' : ''}
        </span>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', margin: '1.5rem 0', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Buscar por nombre..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...inputStyle, minWidth: '220px', flex: '1' }}
        />
        <button
          onClick={() => navigate('/admin/allies/new')}
          style={{
            padding: '0.5rem 1.25rem', background: '#f59e0b', color: 'white',
            border: 'none', borderRadius: '8px', fontWeight: '600',
            fontSize: '0.9rem', cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >
          + Nuevo aliado
        </button>
      </div>

      {error && <p style={{ color: '#dc3545', marginBottom: '1rem' }}>{error}</p>}

      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ background: '#fafafa' }}>
                {['Imagen', 'Nombre', 'Slug', 'Activo', ''].map((h) => (
                  <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#666', fontWeight: '600', whiteSpace: 'nowrap', borderBottom: '1px solid #f0f0f0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} style={{ padding: '2.5rem', textAlign: 'center', color: '#888' }}>Cargando...</td></tr>
              ) : allies.length === 0 ? (
                <tr><td colSpan={5} style={{ padding: '2.5rem', textAlign: 'center', color: '#888' }}>No se encontraron aliados.</td></tr>
              ) : allies.map((ally) => (
                <tr key={ally.id} style={{ borderBottom: '1px solid #f8f8f8' }}>
                  <td style={{ padding: '0.6rem 1rem' }}>
                    {ally.profile_image_url ? (
                      <img src={ally.profile_image_url} alt={ally.name}
                        style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: '50%', border: '1px solid #eee' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f59e0b', fontFamily: "'Gambado Sans', sans-serif", fontSize: '1.2rem' }}>
                        {(ally.name || '?').charAt(0)}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontWeight: '500', color: '#333' }}>{ally.name}</td>
                  <td style={{ padding: '0.75rem 1rem', color: '#888', fontSize: '0.85rem', fontFamily: 'monospace' }}>{ally.slug || '—'}</td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <button
                      onClick={() => handleToggleActive(ally)}
                      disabled={togglingId === ally.id}
                      style={{
                        background: 'none', border: 'none', cursor: togglingId === ally.id ? 'default' : 'pointer',
                        color: togglingId === ally.id ? '#ccc' : ally.is_active ? '#28a745' : '#aaa',
                        fontWeight: '600', fontSize: '0.82rem', padding: 0,
                      }}
                    >
                      {togglingId === ally.id ? '...' : ally.is_active ? 'Activo' : 'Inactivo'}
                    </button>
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                      <Link to={`/admin/allies/${ally.id}`}
                        style={{ color: '#f59e0b', textDecoration: 'none', fontWeight: '500', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                        Editar →
                      </Link>
                      <button
                        onClick={() => handleDelete(ally)}
                        disabled={deletingId === ally.id}
                        style={{
                          background: 'none', border: 'none',
                          color: deletingId === ally.id ? '#ccc' : '#dc3545',
                          cursor: deletingId === ally.id ? 'default' : 'pointer',
                          fontSize: '0.85rem', fontWeight: '500', padding: 0, whiteSpace: 'nowrap',
                        }}
                      >
                        {deletingId === ally.id ? 'Eliminando...' : 'Eliminar'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '1.5rem', justifyContent: 'center' }}>
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
            style={{ padding: '0.5rem 1rem', border: '1px solid #ddd', borderRadius: '8px', background: page === 1 ? '#f8f9fa' : 'white', color: page === 1 ? '#aaa' : '#333', cursor: page === 1 ? 'default' : 'pointer', fontSize: '0.9rem' }}>
            ← Anterior
          </button>
          <span style={{ color: '#666', fontSize: '0.9rem' }}>Página {page} de {totalPages}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            style={{ padding: '0.5rem 1rem', border: '1px solid #ddd', borderRadius: '8px', background: page === totalPages ? '#f8f9fa' : 'white', color: page === totalPages ? '#aaa' : '#333', cursor: page === totalPages ? 'default' : 'pointer', fontSize: '0.9rem' }}>
            Siguiente →
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminAlliesList;
